import React, { useState, useEffect } from 'react';
import { startCase, toLower, values } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

import styles from './Review.module.scss';

import Loader from '../../components/Loader/Loader';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import ReviewSection from '../../components/ReviewSection/ReviewSection';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import {
  getSelectedDestination,
  getSelectedBank,
  getSelectedReinvestment,
  getSelectedDistribution,
  getSelectedDistributionFunds,
  getSelectedDay,
  getSelectedMonths,
  getTaxWithholding,
  getEstimateDate,
} from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import checkmark from '../../assets/images/existing-rmd-checkmark.svg';
import clipboardImg from '../../assets/images/clipboard.svg';
import {
  TAX_WITHHOLDING_STEP,
  FREQUENCY_STEP,
  TOLL_FREE_NUMBER,
  REVIEW_STEPS,
  MONTH_NAMES,
  selectableMonths,
  REVIEW_SUBMIT_STEP,
  ADDRESS_STEP,
  REINVEST_STEP,
  BANK_STEP,
  REINVEST_TO_NEW,
} from '../../settings/settings';
import { uniqueAddressExtractor } from '../Address/Address';
import ReviewTaxwithholdingMessage from './ReviewTaxwithholdingMessage';
import { calculateAndFormatUnixDates } from '../../utils/date-utils';
import {
  openModal as openModalAction,
  ESTIMATED_START_DATE_MODAL,
  TAX_PAYER_MODAL,
  PAPERLESS_TERMS_MODAL,
} from '../../redux/modules/modals/modals.module';
import {
  UPDATE_CHECKBOX,
  SET_TAX_PAYER_LINK_OPENED,
  UPDATE_DISABLE_CHECKBOX,
  SELECTED_PAY_TAXER_WITHHOLDING,
} from '../../redux/modules/shoppingCart/shoppingCart.module';
import { getRemainingRMDAmount } from '../../redux/modules/RMD/RMDSetting.selectors';
import { isCustomerWebViewEnabled } from '../../redux/modules/customer/customer.selectors';
import { getSalesforceRep } from '../../redux/modules/applicationConfig/applicationConfig.selectors';
import { getPersonalInformationData } from '../../redux/modules/PersonalInformation/personalInformation.selectors';
import iconQuestionmark from '../../../src/assets/images/icon-questionmark.svg';

const Review = (props) => {
  const isSalesforceRep = useSelector((state) => getSalesforceRep(state));
  const destination = useSelector(getSelectedDestination);
  const destinationBank = useSelector(getSelectedBank);
  const destinationReinvest = useSelector(getSelectedReinvestment).fund;
  const distribution = useSelector(getSelectedDistribution);
  const distributionFunds = useSelector(getSelectedDistributionFunds);
  const frequencyMonths = useSelector(getSelectedMonths);
  const frequencyDay = useSelector(getSelectedDay);
  const withHolding = useSelector(getTaxWithholding);
  const estimateDate = useSelector(getEstimateDate);
  const remainingRMD = useSelector(getRemainingRMDAmount);
  const personalInformation = useSelector(getPersonalInformationData);
  const showEstimatedDate = estimateDate && remainingRMD > 0;
  const isEdit = useSelector((state) => state.shoppingCart.isEdit);
  const isSubmitLoading = useSelector((state) => state.shoppingCart.isLoading);
  const dispatch = useDispatch();
  const checked = useSelector((state) => state.shoppingCart.checked);
  const disableAlerts = useSelector(
    (state) => state.shoppingCart.disableAlerts
  );
  const reviewSteps =
    destination !== REINVEST_TO_NEW
      ? REVIEW_STEPS.filter((step) => step.label !== 'Personal Information')
      : REVIEW_STEPS;
  const isTaxPayerSelected = useSelector(
    (state) => state.shoppingCart.isTaxPayerSelected
  );
  const isTaxPayerLinkOpened = useSelector(
    (state) => state.shoppingCart.isTaxPayerLinkOpened
  );
  const customerWebViewEnabled = useSelector(isCustomerWebViewEnabled);
  const handleClick = () => {
    dispatch({ type: UPDATE_CHECKBOX, payload: !checked });
  };
  const handleDisableClick = () => {
    dispatch({ type: UPDATE_DISABLE_CHECKBOX, payload: !disableAlerts });
  };

  const iFrame =
    window.location !== window.parent.location &&
    window.location.host.includes('awstrp');
  const [isChecked, setIsChecked] = useState(false);
  const [taxPayerChecked, setTaxPayerChecked] = useState(true);
  const [isClicked, setIsClicked] = useState(false);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    setOpened(isTaxPayerLinkOpened);
    // eslint-disable-next-line
  }, [props.dispatch]);

  function getFunds() {
    let ret = [];
    for (let fundType in distributionFunds) {
      let hasFunds = false;
      let section = { header: '', content: [] };
      let title = fundType.slice(0, fundType.indexOf('Funds'));
      section.header = title.charAt(0).toUpperCase() + title.slice(1) + ' IRA';

      /* Traversing values of fundType (i.e. rolloverFunds, traditionalFunds) object (indexed 0, 1, 2, ...) */
      /*
       ---- Shopping cart structure ----
       ProportionateDistribution:
          funds -> types of funds (i.e. traditional) -> {fund} (curr in below for-loop)
       CustomDistribution:
          funds -> types of funds (i.e. traditional) -> {fund, value} (curr in below for-loop)
       */
      for (let i in distributionFunds[fundType]) {
        let curr = distributionFunds[fundType][i];
        /* CustomDistribution case */
        if (
          curr.value &&
          curr.value > 0 &&
          distribution !== 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
        ) {
          let fundDisplayName = curr.fundName + ' ' + curr.ticker;
          let fundAgreementNumber = curr.agreementNumber;
          let fundDisplayValue = curr.value + '%';
          let div = (
            <span className={styles['blocked_data']}>
              {' '}
              {fundDisplayName} • {fundAgreementNumber} •
              <strong> {fundDisplayValue} </strong>
            </span>
          );
          section.content = [...section.content, div];
          /* ProportionateDistribution case */
        } else if (
          curr.agreementID &&
          distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
        ) {
          let div = (
            <span className={styles['blocked_data']}>
              {' '}
              {curr.fundName}{' '}
              <span className={`${styles['fund-ticker']} orphan-line`}>
                {curr.ticker} •{' '}
                <span className={`${styles['agremnt-num']} blocked_data`}>
                  {curr.agreementNumber}
                </span>
              </span>
            </span>
          );
          section.content = [...section.content, div];
        }
        hasFunds = true;
      }
      if (hasFunds) {
        ret = [...ret, section];
      }
    }

    return ret;
  }

  function getAddress() {
    const uniqueTraditionalAddress = uniqueAddressExtractor(
      values(distributionFunds.traditionalFunds)
        .filter((fund) => {
          return (
            fund.value > 0 ||
            distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
          );
        })
        .map((fund) => {
          return {
            name: fund.formattedEntityNames,
            address: fund.address,
          };
        })
    );
    const uniqueRolloverAddress = uniqueAddressExtractor(
      values(distributionFunds.rolloverFunds)
        .filter((fund) => {
          return (
            fund.value > 0 ||
            distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
          );
        })
        .map((fund) => {
          return {
            name: fund.formattedEntityNames,
            address: fund.address,
          };
        })
    );
    return {
      traditionalAddress: uniqueTraditionalAddress,
      rolloverAddress: uniqueRolloverAddress,
    };
  }

  function parseFromCart(step) {
    let output = '';
    switch (step) {
      case 'Delivery':
        if (destination === 'address') {
        } else if (destination === BANK_STEP && destinationBank) {
          if (destinationBank.name) {
            output = (
              <>
                <div className={`${styles['content-header']} blocked_data`}>
                  Direct Deposit to {destinationBank.name} -{' '}
                  {destinationBank.bankAccountType}{' '}
                  {destinationBank.accountNumber}
                </div>
                <div className={`${styles['bank-info']} blocked_data`}>
                  {destinationBank.registrationLines.map((name, key) => {
                    const txt = `${startCase(toLower(name))}${
                      key === destinationBank.registrationLines.length - 1
                        ? ''
                        : ','
                    }`;
                    return (
                      <span className="blocked_data" key={key}>
                        {' '}
                        {txt}{' '}
                      </span>
                    );
                  })}
                </div>
                <div className={styles['bank-info']}>
                  2-3 business days from withdrawal date
                </div>
              </>
            );
          } else {
            output = <div>Your bank is invalid</div>;
          }
        } else if (destination === REINVEST_STEP) {
          const txt = `${startCase(
            toLower(destinationReinvest.agreementType)
          )}`;
          let div = (
            <div>
              {' '}
              {destinationReinvest.fundName}{' '}
              <span className={`${styles['fund-ticker']} orphan-line`}>
                {destinationReinvest.ticker} •{' '}
                <span className={`${styles['agremnt-num']} blocked_data`}>
                  {destinationReinvest.agreementNumber}
                </span>
              </span>{' '}
            </div>
          );
          output = (
            <>
              <div className={styles['content-header']}>
                RMD reinvested into general investing investment
              </div>
              <div className={styles['reinvest-fund-type']}> {txt} </div>
              {div}{' '}
            </>
          );
        } else if (destination === REINVEST_TO_NEW) {
          const txt = `${startCase(
            toLower(destinationReinvest.agreementType)
          )}`;
          let div = (
            <div>
              {' '}
              {destinationReinvest.fundName}{' '}
              <span className={`${styles['fund-ticker']} orphan-line`}>
                {destinationReinvest.ticker}
                <span className={`${styles['agremnt-num']} blocked_data`}>
                  {destinationReinvest.agreementNumber}
                </span>
              </span>{' '}
              -<strong className={styles['reinvest-to-new-tag']}> NEW</strong>
            </div>
          );
          output = (
            <>
              <div className={styles['content-header']}>
                RMD reinvested into general investing investment
              </div>
              <div className={styles['reinvest-fund-type']}> {txt} </div>
              {div}{' '}
            </>
          );
        }

        break;
      case 'Distribution Method':
        return getFunds();
      case 'Frequency':
        if (frequencyMonths && frequencyDay) {
          let arr = frequencyMonths;
          let day = frequencyDay[0].value;
          output += 'Every ';
          /* Adding month name(s) */
          if (arr.length === 1) {
            output += MONTH_NAMES[arr[0].value];
          } else if (arr.length === 12) {
            output += 'month ';
          } else {
            /* Sorting selected months */
            arr.sort((a, b) => {
              return a && b
                ? selectableMonths[a.value].value -
                    selectableMonths[b.value].value
                : 0;
            });
            for (let i = 0; i < arr.length; i++) {
              if (i === arr.length - 2 && arr.length > 1) {
                output +=
                  MONTH_NAMES[arr[i].value] +
                  ' & ' +
                  MONTH_NAMES[arr[i + 1].value];
                i++;
              } else {
                output += MONTH_NAMES[arr[i].value] + ', ';
              }
            }
          }
          /* Adding day */
          output += ' on the ' + frequencyDay[0].value;
          if (day % 10 === 1 && day !== 11) {
            output += 'st';
          } else if (day % 10 === 2 && day !== 12) {
            output += 'nd';
          } else if (day % 10 === 3 && day !== 13) {
            output += 'rd';
          } else {
            output += 'th';
          }
          return [<span> {output} </span>];
        }
        break;
      default:
        break;
    }
    return output;
  }

  function generateContent(step) {
    switch (step) {
      case 'Personal Information':
        const { dateOfBirth, fullName, primaryAddress, taxID } =
          personalInformation;
        const date = new Date(dateOfBirth);
        const yyyy = date.getFullYear();
        let mm = date.getMonth() + 1;
        let dd = date.getDate();
        if (dd < 10) dd = '0' + dd;
        if (mm < 10) mm = '0' + mm;
        const currDateOfBrith = mm + '/' + dd + '/' + yyyy;
        return (
          <div>
            <p>
              <div className={styles['content-header']}>{fullName}</div>
              <div>
                <p>
                  <span>Date of Birth {currDateOfBrith} </span>
                  {taxID && (
                    <span className={styles['ssn']}>• SSN {taxID}</span>
                  )}
                </p>
                <p>
                  <span>
                    Address {primaryAddress?.street1}, {primaryAddress?.state},{' '}
                    {primaryAddress?.zip}
                  </span>
                </p>
              </div>
            </p>
          </div>
        );
      case 'Delivery':
        if (destination === ADDRESS_STEP) {
          const traditionalAddress = getAddress().traditionalAddress;
          const rolloverAddress = getAddress().rolloverAddress;
          const hasBoth =
            traditionalAddress.length > 0 && rolloverAddress.length > 0;
          return (
            <div
              className={`${styles['delivery-content-container']} ${styles['review-content']}`}
            >
              {hasBoth && (
                <div className={styles['address-header']}>
                  RMD from your Rollover IRA investments will be sent to:
                </div>
              )}
              {rolloverAddress.length > 0 &&
                rolloverAddress.map((fund, key) => {
                  return (
                    <div
                      className={`${styles['address-container']} blocked_data`}
                      key={key}
                    >
                      <div>{fund.name}</div>
                      <div className="blocked_data">
                        {fund.address.fullName}
                      </div>
                      <div className="blocked_data">{fund.address.street1}</div>
                      {fund.address.street2 && (
                        <div className={`${styles.address} blocked_data`}>
                          {fund.address.street2}
                        </div>
                      )}
                      <div className={`${styles.address} blocked_data`}>
                        {fund.address.city}, {fund.address.state}{' '}
                        {fund.address.zip}
                      </div>
                    </div>
                  );
                })}
              {hasBoth && (
                <div className={styles['address-header']}>
                  RMD from your Traditional IRA investments will be sent to:
                </div>
              )}
              {traditionalAddress.length > 0 &&
                traditionalAddress.map((fund, key) => {
                  return (
                    <div
                      className={`${styles['address-container']} blocked_data`}
                      key={key}
                    >
                      <div>{fund.name}</div>
                      <div className="blocked_data">
                        {fund.address.fullName}
                      </div>
                      <div className="blocked_data">{fund.address.street1}</div>
                      {fund.address.street2 && (
                        <div className={`${styles.address} blocked_data`}>
                          {fund.address.street2}
                        </div>
                      )}
                      <div className={`${styles.address} blocked_data`}>
                        {fund.address.city}, {fund.address.state}{' '}
                        {fund.address.zip}
                      </div>
                    </div>
                  );
                })}

              <div>7-10 business days from withdrawal date</div>
            </div>
          );
        } else {
          return (
            <div
              className={`${styles['delivery-content-container']} ${styles['review-content']}`}
            >
              {parseFromCart(step)}
            </div>
          );
        }
      case 'Distribution Method':
        const temp = parseFromCart(step);
        /*
          Note: This check is only for the header, the percentage is not taken off from
          a single CustomDistribution fund with 100% (check MR for screenshot)
         */
        const isOneInvestment =
          temp && temp.length === 1 && temp[0] && temp[0].content.length === 1;
        return (
          <div
            className={`${styles['distribution-content-container']} ${styles['review-content']}`}
          >
            <div className={styles['content-header']}>
              {isOneInvestment
                ? 'RMD withdrawn from one investment'
                : distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
                ? 'Proportionally From eligible IRAs'
                : 'Customized from eligible IRAs'}
            </div>
            {temp.map((element, index) => {
              if (element.content.length > 0) {
                return (
                  <div key={index}>
                    <div className={styles['fund-type']}>
                      {' '}
                      {element.header}{' '}
                    </div>
                    {element.content.map((fund, key) => {
                      return (
                        <span className="blocked_data" key={key}>
                          {' '}
                          {fund} <br />{' '}
                        </span>
                      );
                    })}
                  </div>
                );
              }
              return <div key={index}></div>;
            })}
          </div>
        );
      case 'Frequency':
        return (
          <div
            className={`${styles['content-header']} ${styles['review-content']}`}
          >
            {parseFromCart(step)}
          </div>
        );
      case 'Income Tax Withholding':
        return (
          <div className={styles['review-content']}>
            <ReviewTaxwithholdingMessage {...withHolding} />
          </div>
        );
      case 'Estimated Start Date':
        const dt = new Date().getDate() + 3;
        const after3DAys = new Date().setDate(dt);
        const presentDate = new Date();
        const estimate = new Date(estimateDate);
        const graterthan3 = estimate > after3DAys;
        const lessthantoday = estimate < presentDate;
        return (
          <>
            {`${calculateAndFormatUnixDates(estimateDate, 'MMM DD, YYYY')}`}
            <span className="icon-questionmark">
              <img
                src={iconQuestionmark}
                alt=""
                onClick={() =>
                  dispatch(
                    openModalAction(ESTIMATED_START_DATE_MODAL, {
                      withIn3Days: graterthan3 || lessthantoday,
                    })
                  )
                }
              />
            </span>
          </>
        );
      default:
        return <></>;
    }
  }

  return (
    <div className="rmd-distribution-wrapper">
      {isSubmitLoading && (
        <div className={styles['delete-loading']}>
          <Loader isVisible={true} />
        </div>
      )}
      <div className="sticky-header">
        {isEdit ? (
          <div className={styles['edit-heading']}>Edit Auto-RMD</div>
        ) : (
          <div className="heading">Auto-RMD Setup</div>
        )}
        {!isEdit && (
          <ProgressButtons
            edit={props.edit}
            nextStep={TAX_WITHHOLDING_STEP}
            previousStep={TAX_WITHHOLDING_STEP}
            currentStep={REVIEW_SUBMIT_STEP}
            showBackButton={true}
            backDisabled={false}
            disabled={
              (isClicked || opened) && isChecked && isTaxPayerSelected
                ? false
                : (!isChecked && destination === REINVEST_TO_NEW) ||
                  !checked ||
                  (customerWebViewEnabled && !isSalesforceRep)
            }
            {...props}
          >
            <ProgressBar completedSteps={[0, 1, 2, 3]} activeIndex={4} />
          </ProgressButtons>
        )}
      </div>
      <PageWrapper nextStep={FREQUENCY_STEP} {...props}>
        <div className="scroll-wrapper">
          <div className="rmd-title">
            {isEdit
              ? 'Edit your Auto-RMD details.'
              : 'Almost there! Review your Auto-RMD details.'}
          </div>
          <div className={styles['option-container']}>
            <div className={styles['review-list']}>
              {reviewSteps.map((step, i) => {
                return (
                  <ReviewSection
                    key={i}
                    step={step}
                    lineType={
                      !showEstimatedDate && isEdit && i === 4 ? 'none' : 'light'
                    }
                    content={generateContent(step.label)}
                    {...props}
                    edit={i !== 1}
                  />
                );
              })}
              {showEstimatedDate && (
                <ReviewSection
                  step={{ label: 'Estimated Start Date' }}
                  edit={false}
                  lineType={isEdit ? 'none' : 'dark'}
                  content={generateContent('Estimated Start Date')}
                />
              )}
              {!isEdit && (
                <div className={styles['legal-section']}>
                  <div className={styles['checked-box']}>
                    <input
                      onClick={handleClick}
                      checked={checked}
                      type="checkbox"
                      aria-label='check box required'
                    />
                  </div>
                  <span className={styles['legal']}>
                    <strong> REQUIRED: </strong>I understand that I am
                    responsible for calculating and withdrawing my RMD amounts
                    under Internal Revenue Code Section 401(a)(9). I hereby
                    indemnify and hold T. Rowe Price Trust Company, its parent
                    and affiliates, successors, employees, and the T. Rowe Price
                    funds harmless from any and all liability should I fail to
                    receive the correct RMD amount in any calendar year.
                  </span>
                </div>
              )}
              {iFrame && (
                <div className={styles['disable-alerts-section']}>
                  <div className={styles['checked-box']}>
                    <input
                      onClick={handleDisableClick}
                      checked={disableAlerts}
                      type="checkbox"
                    />
                    <span className={styles['legal']}>
                      Disable confirmations and real-time alerts.
                    </span>
                  </div>
                </div>
              )}
              {destination === REINVEST_TO_NEW && (
                <p className={styles['legal-notice-container']}>
                  <p className={styles['content-header']}>
                    New Account Consent
                  </p>
                  <div>
                    To complete this online account application, you must review
                    and consent to the electronic access and delivery
                    requirements and agree to the terms and conditions of the
                    Documents and Disclosures in the link below. “I” or “we”
                    refers to all account owners.
                  </div>
                  <p>
                    <p className={styles['content-header']}>
                      I. Consent to Electronic Access and Delivery
                    </p>
                    <div>
                      <p>
                        By clicking "Submit" at the end of these Terms and
                        Conditions, I certify that I am able to access all
                        documents and disclosures online without additional
                        hardware or software, and I consent to establish the
                        account(s) electronically in the name listed on this
                        online application and to receive all documents and
                        disclosures that are necessary to open a new account
                        in electronic form, including, but not limited to,
                        the Customer Account Agreement, the T. Rowe Price
                        Investment Services, Inc. Form CRS, the T. Rowe Price
                        Advisory Services, Inc. Form CRS, the T. Rowe Price
                        Privacy Policy, and the T. Rowe Price Traditional and
                        Roth IRA Disclosure Statement and Custodial Agreement 
                        (applicable only to IRA accounts). Electronic versions
                        of these documents and disclosures are provided below.
                      </p>{' '}
                      <p>
                        {' '}
                        In addition, I consent to subscribe to T. Rowe Price 
                        Paperless Services ("Paperless Services") to receive 
                        all T. Rowe Price mutual fund (and T. Rowe Price 
                        Brokerage plans that T. Rowe Price 
                        manages, if applicable) statements, confirmations, 
                        prospectuses, shareholder reports, proxy materials, 
                        tax forms, and client service 
                        communications (as applicable) for all of my eligible 
                        accounts electronically instead of a paper copy and 
                        acknowledge that I have read and agree to the &nbsp;
                        <button
                          id="paperless-terms-link"
                          className={styles['paperless-optin-terms-link']}
                          type="button"
                          onClick={() => dispatch(openModalAction(PAPERLESS_TERMS_MODAL))}
                        >
                          Electronic Consent to Paperless Services Terms and Conditions
                        </button>
                        ("Paperless Terms"). I understand (i) my consent to 
                        participate in Paperless Services will apply to all 
                        existing and future mutual fund, Brokerage 
                        accounts (as applicable) on which 
                        I am the primary account holder or authorized party 
                        subject to certain conditions outlined in the Paperless 
                        Terms; (ii) in the case of accounts with multiple owners, 
                        I represent that I have the authority to act on behalf of 
                        all owners and consent to Paperless Services on behalf of 
                        all owners and for all documents (with certain exceptions 
                        described in the Paperless Terms) to be sent to the email 
                        address I have designated; (iii) I will receive an email 
                        alerting me that my documents are available online to view, 
                        print, and/or download and I may need to access some 
                        documents securely by logging into my account online; 
                        (iv) all of the documents necessary to open an account online, 
                        as well as the documents available via Paperless Services, 
                        are available in portable document format (PDF) and that in 
                        order to access PDF files, I must have Adobe® Acrobat® 
                        Reader software (available for download at no cost at 
                        adobe.com) and that if I do not have the ability to access, 
                        print, and retain PDF files, I will not open an account 
                        online; (v) I may request a paper copy of these documents 
                        at any time by calling 1-800-225-5132; and (vi) for 
                        security purposes, I must have 128-bit encryption when 
                        gaining access to accounts online and although most 
                        Internet Service Providers allow unlimited usage, some 
                        may charge or have usage limits, and I am responsible 
                        for additional charges incurred.
                      </p>
                      <p>
                        {' '}
                        My consent to Paperless Services is effective until 
                        revocation by me, unless I am unsubscribed by T. 
                        Rowe Price as described in the Paperless Terms. I 
                        may unsubscribe at any time by going to the Paperless 
                        Preferences page in the Profile section of Account Access.
                      </p>
                      <p>
                        {' '}
                        I certify that I am the person named in this online 
                        account application and I affirm that I have provided 
                        a valid email address.
                      </p>
                    </div>
                  </p>

                  {/* Second point  */}
                  <p>
                    <p className={styles['content-header']}>
                      II. Documents and Disclosures
                    </p>
                    <div>
                      <strong>
                        1. Open and review the Documents and Disclosures PDF to
                        complete the online account opening process:
                      </strong>
                    </div>
                    {opened ? (
                      <button
                        className={`${styles['review-disclosure']} ${styles['review-disclosure-opened-button']}`}
                        onClick={(e) => {
                          window.open(
                            'https://www.troweprice.com/personal-investing/tools/nasu/static/media/MFDisclosures_202212.pdf',
                            '_blank'
                          );
                        }}
                      >
                        <img
                          src={checkmark}
                          width="18"
                          className={styles['review-disclosure-checked2']}
                          alt="checkbox"
                        />{' '}
                        <span className={styles['withholding-button-text']}>
                          Opened
                        </span>
                      </button>
                    ) : (
                      <button
                        id="review-disclosure-button"
                        className={
                          isClicked
                            ? styles['review-disclosure-clicked']
                            : styles['review-disclosure']
                        }
                        onClick={(e) => {
                          setOpened(isClicked);
                          setIsClicked(true);
                          dispatch({ type: SET_TAX_PAYER_LINK_OPENED });
                          window.open(
                            'https://www.troweprice.com/personal-investing/tools/nasu/static/media/MFDisclosures_202212.pdf',
                            '_blank'
                          );
                        }}
                      >
                        {isClicked && (
                          <img
                            src={checkmark}
                            className={styles['review-disclosure-checked2']}
                            alt="checkbox"
                          />
                        )}

                        {isClicked
                          ? 'Documents & Disclosures Reviewed'
                          : 'Review Documents & Disclosures'}
                        {!isClicked && (
                          <img
                            className={styles['review-disclosure-clipboard']}
                            src={clipboardImg}
                            alt="clipboard"
                          />
                        )}
                      </button>
                    )}
                    <div className={styles['tax-payer-certifications']}>
                      <p className={styles['content-header']}>
                        III. Taxpayer Certifications
                      </p>
                      <p>
                        By clicking "Submit" below, under penalties of perjury,
                        I certify that:{' '}
                      </p>
                      <ol>
                        <li>
                          The number I have entered is my correct taxpayer
                          identification number; AND
                        </li>
                        <li>
                          I am not subject to backup withholding because:
                          <ol type="a">
                            <li> I am exempt from backup withholding; OR</li>
                            <li>
                              I have not been notified by the Internal Revenue
                              Service (IRS) that I am subject to backup
                              withholding as a result of a failure to report all
                              interest and dividends; Or
                            </li>
                            <li>
                              The IRS has notified me that I am no longer
                              subject to backup withholding.
                            </li>
                          </ol>
                        </li>
                        <li>
                          I am a U.S. citizen or other U.S. person (as defined
                          in the IRS Form W-9 instructions).
                        </li>
                      </ol>

                      <p>
                        <span
                          onClick={(e) => {
                            dispatch(openModalAction(TAX_PAYER_MODAL));
                          }}
                          className={styles['error-link']}
                        >
                          <strong>
                            Click here ONLY if you have been identified by the
                            IRS that you are subject to backup withholding
                            because you have failed to report all interest and
                            dividends on your tax returns
                          </strong>
                        </span>
                        <br />
                        {isTaxPayerSelected && (
                          <div
                            className={`${styles['checked-box']} ${styles['taxpayer-checbox-con']}`}
                          >
                            <input
                              onClick={(e) => {
                                dispatch({
                                  type: SELECTED_PAY_TAXER_WITHHOLDING,
                                  payload: false,
                                });
                              }}
                              checked={isTaxPayerSelected}
                              type="checkbox"
                            />
                            <span className={styles['legal']}>
                              I acknowledge that I am subject to backup
                              withdrawing on all future distributions{' '}
                            </span>
                          </div>
                        )}
                        <br />
                        <div>
                          <p>
                            If I fail to give the correct number and provide
                            certification by clicking “Submit” below, then T.
                            Rowe Price may reject, restrict, or redeem my
                            account. I may also be subject to backup
                            withholding, and I may be subject to an IRS penalty.
                            For clarification on any of these certification
                            issues, please contact a T. Rowe Price associate at
                            (800) 225-5132.
                          </p>
                          The IRS does not require your consent to any provision
                          of this document other than the certification required
                          to avoid backup withholding.{' '}
                        </div>
                      </p>
                    </div>
                    <p>
                      I acknowledge that I agree to read and be bound my the
                      terms of the Account Agreements outlined in the Brokerage
                      Account Welcome Handbook, which can be found online at
                      http://www.troweprice.com and will be sent to me after I
                      open an account and which are incorporated herein. To
                      request a copy in advance, please call 1-800-638-5660 if I
                      do not understand any provision therein, I will notify T.
                      Rowe Price Investment Services. I have the authority and
                      legal capacity to purchase mutual fund shares, am of legal
                      age in my state, and believe each investment is suitable
                      for me.
                    </p>
                  </p>
                  <div>
                    <strong>
                      2. You must acknowledge and agree to the terms and
                      conditions specified below.
                    </strong>

                    <p
                      className={`${styles['legal-section']} ${styles['legal-section2']}`}
                    >
                      <div
                        className={`${styles['checked-box']} ${styles['checked-box2']}`}
                      >
                        <input
                          aria-label='consent-electronic-delivery'
                          onClick={(e) => setIsChecked(!isChecked)}
                          checked={isChecked}
                          type="checkbox"
                        />
                      </div>
                      <span className={styles['legal-reinvest']}>
                        I acknowledge and agree to the terms and conditions of
                        the Consent to Electronic Access and Delivery and the
                        Documents and Disclosures in this online account
                        application.
                      </span>
                    </p>
                  </div>
                </p>
              )}
            </div>
            <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};
export default Review;
